import { Shield, Group, Groups } from "@mui/icons-material";
import axiosService from "../axios";
import type { IRanking, IRecipe, IRecipeIngredient, IRecipeVisibility, ISavedRecipes } from "../../types/recipe";
import axios from "axios";
import type { IEditData } from "../../reducers/editDataReducer";
import type { Dispatch } from "react";
import type { UnknownAction } from "redux";
import { addNameRecipe } from "../../store/slices/recipes";
import { cleanJsonResponse } from "./generalHelper";

export const fetchRecipes = () => {
    return axiosService.get(`${process.env.REACT_APP_API_URL}/recipes/fetchSaved`);
};

export const visibilityOptions: Record<
    IRecipeVisibility,
    {
        value: IRecipeVisibility;
        label: string;
        Icon: React.ElementType;
    }
> = {
    personal: { value: "personal", label: "Personal", Icon: Shield },
    private: { value: "private", label: "Private", Icon: Group },
    public: { value: "public", label: "Public", Icon: Groups },
};

export const visibilityLabels = {
    personal: "Only you can see it!",
    private: "Only followers can see it!",
    public: "Everyone can see it!",
};

export const setRecipeInfo = (res: any) => {
    try {
        return {
            name: res.data.body.name,
            description: res.data.body.description,
            ingredients: res.data.body.ingredients,
            procedure: res.data.body.procedure,
            tags: res.data.body.tags,
            user: { username: res.data.body.user.username },
            createdAt: res.data.body.createdAt,
            savedAt: res.data.body.savedAt,
            _id: res.data.body._id,
            image: res.data.body.image,
            images: res.data.body.images,
            likes: res.data.body.likes,
            comments: res.data.body.comments,
            ranking: res.data.body.ranking,
        } as ISavedRecipes;
    } catch (error) {
        return {
            createdAt: "",
            savedAt: "",
            description: "",
            tags: [],
            ingredients: [],
            name: "",
            procedure: [],
            _id: "",
            likes: [],
            comments: [],
            ranking: [],
            user: { username: "" },
            image: undefined,
            images: [],
        };
    }
};

export const fetchRecipeById = (recipeId: string) => {
    return axios.get(`${process.env.REACT_APP_API_URL}/recipes/fetchById`, { params: { routeId: recipeId } });
};

export const openRecipeModal = (
    recipeId: string,
    setCurrentRecipe: (recipe: ISavedRecipes) => void,
    setOpenRecipeModal: (b: boolean) => void
) => {
    return fetchRecipeById(recipeId).then((res) => {
        const recipeInfo: ISavedRecipes = setRecipeInfo(res);

        setCurrentRecipe(recipeInfo);
        setOpenRecipeModal(true);
    });
};

export const filterProcedureTimer = (step: string) => {
    const timeUnits = ["minutes", "minute", "seconds", "second", "hours", "hour"];

    // Allow patterns like "2 minutes", "2-3 minutes", "2 – 3 minutes", "2.5-3 minutes"
    const regex = new RegExp(`(\\d+(?:[.,]\\d+)?(?:\\s*[-–]\\s*\\d+(?:\\.\\d+)?)?\\s*(?:${timeUnits.join("|")}))`);

    // Split into [before, match, after, ...]
    const parts = step.split(regex).filter(Boolean);

    return parts.map((part) => ({
        text: part.trim(),
        isTime: regex.test(part),
    }));
};

export const parseUpperBoundDuration = (input: string): { minutes: number; seconds: number } => {
    const s = input.toLowerCase().trim().replace(/[–—−]/g, "-"); // normalize dashes

    // Try ranges first (e.g. "6-7 minutes", "10-20 s")
    let m = s.match(/(\d+)\s*-\s*(\d+)\s*(?:minutes?|mins?\.?|m)\b/) || s.match(/(\d+)\s*-\s*(\d+)\s*(?:seconds?|secs?\.?|s)\b/);

    if (m) {
        const upper = parseInt(m[2], 10);
        const isMinutes = /min/.test(m[0]) || /\bm\b/.test(m[0]);
        return isMinutes ? { minutes: upper, seconds: 0 } : { minutes: 0, seconds: upper };
    }

    // Then singles (e.g. "5 minutes", "5 sec")
    m = s.match(/(\d+)\s*(?:minutes?|mins?\.?|m)\b/) || s.match(/(\d+)\s*(?:seconds?|secs?\.?|s)\b/);

    if (m) {
        const val = parseInt(m[1], 10);
        const isMinutes = /min/.test(m[0]) || /\bm\b/.test(m[0]);
        return isMinutes ? { minutes: val, seconds: 0 } : { minutes: 0, seconds: val };
    }

    // Fallback (nothing recognized)
    return { minutes: 0, seconds: 0 };
};

export const addChapterIngredientTransform = (recipe: ISavedRecipes | IRecipe | IEditData) => {
    const transformedRecipe = {
        ...recipe,
        ingredients: recipe.ingredients.map((ingredient: IRecipeIngredient) => ({ ...ingredient, ingredient: ingredient.ingredient?._id })),
    };

    return transformedRecipe;
};

export const getIngredientAmountMap = () => {
    return [
        { value: "g", label: "grams" },
        { value: "ml", label: "milliliter" },
        { value: "tblsp", label: "tablespoon" },
        { value: "tsp", label: "teaspoon" },
    ];
};

export const getRankingAverage = (rankings: IRanking[]) => {
    if (!rankings || rankings.length === 0) return 0;

    const rankValues = rankings.map((ranking: IRanking) => ranking.value);
    const rankAverage = rankValues.reduce((acc, n) => acc + n, 0) / rankings.length;

    return rankAverage;
};

export const generateRecipeByName = (recipeName: string, user: string | null | undefined, dispatch: Dispatch<UnknownAction>) => {
    return axios
        .post(`${process.env.REACT_APP_API_URL}/recipes/generate/name`, {
            recipeName: recipeName,
            username: user,
        })
        .then((res) => {
            const cleanedRecipe = cleanJsonResponse(res.data.body.data);

            let recipeJSON;
            try {
                recipeJSON = JSON.parse(cleanedRecipe);
            } catch (error) {
                console.error("Failed to parse generated recipe:", error);
                throw new Error("Invalid recipe format received from server");
            }

            dispatch(
                addNameRecipe({
                    name: recipeJSON.name,
                    description: recipeJSON.description,
                    ingredients: recipeJSON.ingredients,
                    tags: recipeJSON.tags,
                    procedure: recipeJSON.procedure,
                    generationId: res.data.body.generationId,
                })
            );
        });
};
